import Dialog from "../../components/General/ConfirmableDialog";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  DialogActions,
  IconButton,
  MenuItem,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { apiVariable } from "../../api/apiVariable";
import useAuth from "../../hooks/useAuth";
import {
  EmployeeDto,
  EmployeePositionHistoryDto,
  PromoteEmployeeDto,
  UpdatePositionHistoryDto,
} from "../../interfaces/HR/employee";
import StatusSnackbar from "../../components/General/StatusSnackbar/StatusSnackbar";

const authHeaders = (token: string | null | undefined) => ({
  headers: { Authorization: `Bearer ${token}` },
});

const fetchEmployees = async (token: string | null | undefined) => {
  const { data } = await axios.get<EmployeeDto[]>(
    `${apiVariable}/getAllEmployees`,
    {
      ...authHeaders(token),
      params: { areActive: true },
    },
  );
  return data;
};

const fetchPositionHistory = async (
  token: string | null | undefined,
  employeeId: string,
) => {
  const { data } = await axios.get<EmployeePositionHistoryDto[]>(
    `${apiVariable}/hr/employees/${employeeId}/position-history`,
    authHeaders(token),
  );
  return data;
};

const promoteEmployee = async ({
  token,
  employeeId,
  promotion,
}: {
  token: string | null | undefined;
  employeeId: string;
  promotion: PromoteEmployeeDto;
}) => {
  const { data } = await axios.put<EmployeeDto>(
    `${apiVariable}/promoteEmployee/${employeeId}`,
    promotion,
    authHeaders(token),
  );
  return data;
};

const EMPTY_VALUE = "—";
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

const updatePositionHistory = async ({
  token,
  historyId,
  request,
}: {
  token: string | null | undefined;
  historyId: string;
  request: UpdatePositionHistoryDto;
}) => {
  const { data } = await axios.put<EmployeePositionHistoryDto>(
    `${apiVariable}/hr/position-history/${historyId}`,
    request,
    authHeaders(token),
  );
  return data;
};

const deletePositionHistory = async ({
  token,
  historyId,
}: {
  token: string | null | undefined;
  historyId: string;
}) => {
  await axios.delete(
    `${apiVariable}/hr/position-history/${historyId}`,
    authHeaders(token),
  );
};

const getErrorMessage = (error: any) => {
  const data = error?.response?.data;

  if (typeof data === "string") return data;
  if (typeof data === "object" && data !== null) {
    if (typeof data.message === "string") return data.message;
    if (typeof data.error === "string") return data.error;

    try {
      return JSON.stringify(data);
    } catch {
      return "An unexpected error occurred.";
    }
  }

  return error?.message || "An unexpected error occurred.";
};

const formatDate = (date?: string | null) => {
  if (!date) return "Present";
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(new Date(date));
};

const EmployeeHistoryPage = () => {
  const { token, role } = useAuth();
  const queryClient = useQueryClient();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [isPromoteOpen, setIsPromoteOpen] = useState(false);
  const [newPosition, setNewPosition] = useState("");
  const [promotionDate, setPromotionDate] = useState("");
  const [managerId, setManagerId] = useState<string | null>(null);
  const [department, setDepartment] = useState<string | null>(null);
  const [editingHistory, setEditingHistory] =
    useState<EmployeePositionHistoryDto | null>(null);
  const [deletingHistory, setDeletingHistory] =
    useState<EmployeePositionHistoryDto | null>(null);
  const [editForm, setEditForm] = useState({
    position: "",
    managerId: "",
    departmentName: "",
    departmentId: "",
    startDate: "",
    endDate: "",
  });
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const canManagePositionHistory = role?.includes("HR") ?? false;

  const {
    data: employees = [],
    isLoading: employeesLoading,
    isError: employeesError,
  } = useQuery({
    queryKey: ["employees", "position-history"],
    queryFn: () => fetchEmployees(token),
    enabled: Boolean(token),
  });

  const selectedEmployee = employees.find(
    (employee) => employee.id === selectedEmployeeId,
  );

  const managerOptions = useMemo(
    () => employees.filter((employee) => employee.id !== selectedEmployeeId),
    [employees, selectedEmployeeId],
  );

  const departmentOptions = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...employees.map((employee) => employee.department),
            editingHistory?.departmentId,
            editingHistory?.departmentName,
            editingHistory?.department,
          ].filter(Boolean) as string[],
        ),
      ).sort(),
    [employees, editingHistory],
  );

  const refreshPositionHistory = (employeeId = selectedEmployeeId) => {
    if (employeeId) {
      queryClient.invalidateQueries({
        queryKey: ["employee-position-history", employeeId],
      });
    }
  };

  const selectedManager =
    managerOptions.find((employee) => employee.id === managerId) ?? null;

  const {
    data: positionHistory = [],
    isLoading: historyLoading,
    isError: historyError,
  } = useQuery({
    queryKey: ["employee-position-history", selectedEmployeeId],
    queryFn: () => fetchPositionHistory(token, selectedEmployeeId),
    enabled: Boolean(token) && Boolean(selectedEmployeeId),
  });

  const promotionMutation = useMutation({
    mutationFn: promoteEmployee,
    onSuccess: (employee) => {
      refreshPositionHistory(employee.id);
      queryClient.invalidateQueries({
        queryKey: ["employees", "position-history"],
      });
      setIsPromoteOpen(false);
      setNewPosition("");
      setPromotionDate("");
      setManagerId(null);
      setDepartment(null);
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(getErrorMessage(error));
    },
  });

  const updateMutation = useMutation({
    mutationFn: updatePositionHistory,
    onSuccess: () => {
      setStatus("success");
      setSuccessMessage("Position history updated successfully");
      setEditingHistory(null);
      refreshPositionHistory();
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(
        getErrorMessage(error) || "Position history could not be updated.",
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deletePositionHistory,
    onSuccess: () => {
      setStatus("success");
      setSuccessMessage("Position history deleted successfully");
      setDeletingHistory(null);
      refreshPositionHistory();
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(
        getErrorMessage(error) || "Position history could not be deleted.",
      );
    },
  });

  const isActionableHistory = (history: EmployeePositionHistoryDto) =>
    Boolean(history.id && history.id !== EMPTY_GUID && history.endDate);

  const columns = useMemo<GridColDef<EmployeePositionHistoryDto>[]>(
    () => [
      { field: "position", headerName: "Position", flex: 1, minWidth: 220 },
      {
        field: "managerName",
        headerName: "Manager",
        flex: 1,
        minWidth: 180,
        valueGetter: (_, row) => row.managerName || EMPTY_VALUE,
      },
      {
        field: "department",
        headerName: "Department",
        flex: 1,
        minWidth: 180,
        valueGetter: (_, row) =>
          row.departmentName || row.department || EMPTY_VALUE,
      },
      {
        field: "startDate",
        headerName: "Start Date",
        minWidth: 160,
        valueFormatter: (value) => formatDate(value as string),
      },
      {
        field: "endDate",
        headerName: "End Date",
        minWidth: 160,
        valueFormatter: (value) => formatDate(value as string | null),
      },
      { field: "duration", headerName: "Duration", flex: 1, minWidth: 180 },
      ...(canManagePositionHistory
        ? [
            {
              field: "actions",
              headerName: "Actions",
              sortable: false,
              filterable: false,
              minWidth: 120,
              renderCell: ({ row }: { row: EmployeePositionHistoryDto }) =>
                isActionableHistory(row) ? (
                  <Stack
                    flexWrap={{ xs: "wrap", lg: "nowrap" }}
                    useFlexGap
                    direction="row"
                    spacing={1}
                  >
                    <Tooltip title="Update position history">
                      <IconButton
                        aria-label="Update position history"
                        size="small"
                        onClick={() => handleOpenEdit(row)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete position history">
                      <IconButton
                        aria-label="Delete position history"
                        size="small"
                        color="error"
                        onClick={() => setDeletingHistory(row)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : null,
            } as GridColDef<EmployeePositionHistoryDto>,
          ]
        : []),
    ],
    [canManagePositionHistory],
  );

  const handleOpenEdit = (history: EmployeePositionHistoryDto) => {
    setEditingHistory(history);
    setEditForm({
      position: history.position ?? "",
      managerId: history.managerId ?? "",
      departmentName:
        history.departmentId ??
        history.departmentName ??
        history.department ??
        "",
      departmentId:
        history.departmentId ??
        history.departmentName ??
        history.department ??
        "",
      startDate: history.startDate?.split("T")[0] ?? "",
      endDate: history.endDate?.split("T")[0] ?? "",
    });
  };

  const handleUpdateHistory = () => {
    if (
      !editingHistory ||
      !editForm.position.trim() ||
      !editForm.startDate ||
      updateMutation.isPending
    )
      return;
    updateMutation.mutate({
      token,
      historyId: editingHistory.id,
      request: {
        position: editForm.position.trim(),
        managerId: editForm.managerId || null,
        departmentId: editingHistory.departmentId
          ? editForm.departmentName || null
          : null,
        departmentName: editingHistory.departmentId
          ? null
          : editForm.departmentName || null,
        startDate: editForm.startDate,
        endDate: editForm.endDate || null,
        duration: editingHistory.duration,
      },
    });
  };

  const handleDeleteHistory = () => {
    if (!deletingHistory || deleteMutation.isPending) return;
    deleteMutation.mutate({ token, historyId: deletingHistory.id });
  };

  const handleOpenPromote = () => {
    setNewPosition(selectedEmployee?.position ?? "");
    setPromotionDate(new Date().toISOString().slice(0, 10));
    setManagerId(
      selectedEmployee?.manager?.id ?? selectedEmployee?.reportsToId ?? null,
    );
    setDepartment(selectedEmployee?.department ?? null);
    setIsPromoteOpen(true);
  };

  const handlePromote = () => {
    if (!selectedEmployeeId || !newPosition.trim()) return;

    promotionMutation.mutate({
      token,
      employeeId: selectedEmployeeId,
      promotion: {
        newPosition: newPosition.trim(),
        promotionDate: promotionDate || null,
        managerId,
        department,
      },
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Employee History
          </Typography>
          <Typography color="text.secondary">
            Review employee position history and record promotions.
          </Typography>
        </Box>

        {employeesError && (
          <Alert severity="error">Unable to load employees.</Alert>
        )}

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Autocomplete
                options={employees}
                loading={employeesLoading}
                getOptionLabel={(employee) =>
                  `${employee.firstName} ${employee.lastName} • ${employee.position}`
                }
                value={selectedEmployee ?? null}
                onChange={(_, employee) =>
                  setSelectedEmployeeId(employee?.id ?? "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee"
                    placeholder="Select employee"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {employeesLoading ? (
                            <CircularProgress size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {selectedEmployee && (
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems="center"
                >
                  <Chip
                    icon={<WorkHistoryIcon />}
                    label={`Current: ${selectedEmployee.position}`}
                  />
                  {selectedEmployee.timeInCurrentPosition && (
                    <Chip
                      label={`Time in role: ${selectedEmployee.timeInCurrentPosition}`}
                    />
                  )}
                  {selectedEmployee.currentPositionStartDate && (
                    <Chip
                      label={`Started: ${formatDate(selectedEmployee.currentPositionStartDate)}`}
                    />
                  )}
                  <Box flexGrow={1} />
                  <Button
                    variant="contained"
                    startIcon={<TrendingUpIcon />}
                    onClick={handleOpenPromote}
                  >
                    Promote Employee
                  </Button>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Position History
            </Typography>
            {!selectedEmployeeId ? (
              <Alert severity="info">
                Select an employee to view position history.
              </Alert>
            ) : historyError ? (
              <Alert severity="error">Unable to load position history.</Alert>
            ) : (
              <DataGrid
                autoHeight
                rows={positionHistory}
                columns={columns}
                loading={historyLoading}
                disableRowSelectionOnClick
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[10, 25, 50]}
              />
            )}
          </CardContent>
        </Card>
      </Stack>

      <Dialog
        open={isPromoteOpen}
        onClose={() => setIsPromoteOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Promote Employee</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Employee"
              value={
                selectedEmployee
                  ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
                  : ""
              }
              disabled
            />
            <TextField
              label="New Position"
              value={newPosition}
              onChange={(event) => setNewPosition(event.target.value)}
              required
              autoFocus
            />
            <TextField
              label="Promotion Date"
              type="date"
              value={promotionDate}
              onChange={(event) => setPromotionDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Autocomplete
              options={managerOptions}
              value={selectedManager}
              onChange={(_, manager) => setManagerId(manager?.id ?? null)}
              getOptionLabel={(employee) =>
                `${employee.firstName} ${employee.lastName} • ${employee.position}`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Manager"
                  placeholder="Select manager"
                />
              )}
            />
            <Autocomplete
              options={departmentOptions}
              value={department}
              onChange={(_, selectedDepartment) =>
                setDepartment(selectedDepartment)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Department"
                  placeholder="Select department"
                />
              )}
            />
            {promotionMutation.isError && (
              <Alert severity="error">Promotion could not be saved.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPromoteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePromote}
            disabled={!newPosition.trim() || promotionMutation.isPending}
          >
            {promotionMutation.isPending ? "Saving..." : "Save Promotion"}
          </Button>
        </DialogActions>
      </Dialog>
      {canManagePositionHistory && (
        <Dialog
          open={Boolean(editingHistory)}
          onClose={() => !updateMutation.isPending && setEditingHistory(null)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Update Position History</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Position"
                value={editForm.position}
                onChange={(event) =>
                  setEditForm({ ...editForm, position: event.target.value })
                }
                required
                autoFocus
              />
              <TextField
                select
                label="Manager"
                value={editForm.managerId}
                onChange={(event) =>
                  setEditForm({ ...editForm, managerId: event.target.value })
                }
                helperText="Leave empty if this record has no manager."
              >
                <MenuItem value="">{EMPTY_VALUE}</MenuItem>
                {editingHistory?.managerId &&
                  !managerOptions.some(
                    (manager) => manager.id === editingHistory.managerId,
                  ) && (
                    <MenuItem value={editingHistory.managerId}>
                      {editingHistory.managerName || editingHistory.managerId}
                    </MenuItem>
                  )}
                {managerOptions.map((manager) => (
                  <MenuItem key={manager.id} value={manager.id}>
                    {manager.firstName} {manager.lastName} | {manager.position}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select={departmentOptions.length > 0}
                label="Department"
                value={editForm.departmentName}
                onChange={(event) =>
                  setEditForm({
                    ...editForm,
                    departmentName: event.target.value,
                  })
                }
                helperText="Leave empty if this record has no department."
              >
                <MenuItem value="">{EMPTY_VALUE}</MenuItem>
                {departmentOptions.map((department) => (
                  <MenuItem key={department} value={department}>
                    {department}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Start Date"
                type="date"
                value={editForm.startDate}
                onChange={(event) =>
                  setEditForm({ ...editForm, startDate: event.target.value })
                }
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                label="End Date"
                type="date"
                value={editForm.endDate}
                onChange={(event) =>
                  setEditForm({ ...editForm, endDate: event.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
              {updateMutation.isError && (
                <Alert severity="error">
                  {errorMessage || "Position history could not be updated."}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEditingHistory(null)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdateHistory}
              disabled={
                !editForm.position.trim() ||
                !editForm.startDate ||
                updateMutation.isPending
              }
            >
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {canManagePositionHistory && (
        <Dialog
          open={Boolean(deletingHistory)}
          onClose={() => !deleteMutation.isPending && setDeletingHistory(null)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this position history record?
            </Typography>
            {deleteMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorMessage || "Position history could not be deleted."}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeletingHistory(null)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteHistory}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <StatusSnackbar
        status={status}
        successMessage={successMessage}
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
    </Box>
  );
};

export default EmployeeHistoryPage;
